import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Logo from '@/components/feature/Logo';
import PractitionerCard from '@/pages/recherche/components/PractitionerCard';
import Pagination from '@/pages/recherche/components/Pagination';
import BookingModal from '@/pages/recherche/components/BookingModal';
import Breadcrumbs from '@/pages/recherche/components/Breadcrumbs';
import NotFound from '@/pages/NotFound';
import { fetchPractitionersFromApi, fetchSearchOptionsFromApi } from '@/lib/api';
import { mapPractitionerRow, TIER_ORDER } from '@/lib/practitioner';
import { specialties, districts } from '@/mocks/practitioners';
import { CITY_NAME, CITY_SLUG, buildSearchPath, findByNameSlug } from '@/lib/seo';
import { trackSearch } from '@/lib/analytics';
import type { Practitioner } from '@/mocks/practitioners';

export default function RecherchePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const params = useParams();
  const spString = searchParams.toString();

  // Filtres issus de l'URL « propre » (ex. /rabat/agdal/cardiologue).
  const paramDistrict = findByNameSlug(districts, params.district);
  const paramSpecialty = findByNameSlug(specialties, params.specialty);
  const invalidCity = Boolean(params.city) && params.city !== CITY_SLUG;

  const [specialty, setSpecialty] = useState(paramSpecialty || searchParams.get('specialty') || '');
  const [district, setDistrict] = useState(paramDistrict || searchParams.get('district') || '');
  const [nameQuery, setNameQuery] = useState(searchParams.get('q') || '');
  const [showSpec, setShowSpec] = useState(false);
  const [showDist, setShowDist] = useState(false);
  const [booking, setBooking] = useState<Practitioner | null>(null);
  const [allPractitioners, setAllPractitioners] = useState<Practitioner[]>([]);
  const [filterOptions, setFilterOptions] = useState({ cities: [CITY_NAME], specialties, districts });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  // Pagination : 25 praticiens maximum affichés par page.
  const PAGE_SIZE = 25;
  const [page, setPage] = useState(1);

  // Point d'ancrage pour remonter en haut de la liste au changement de page.
  const resultsTopRef = useRef<HTMLDivElement>(null);

  // Évite d'envoyer deux fois le même événement de recherche à Google Analytics.
  const trackedSearchRef = useRef('');

  // Envoie l'événement de recherche (ville / quartier / spécialité) une seule fois
  // par combinaison affichée.
  const sendSearchEvent = (
    nextDistrict: string,
    nextSpecialty: string,
    nextName: string,
    count: number,
  ) => {
    const name = nextName.trim();
    if (!nextDistrict && !nextSpecialty && !name) return;
    const signature = `${nextDistrict}|${nextSpecialty}|${name.toLowerCase()}`;
    if (signature === trackedSearchRef.current) return;
    trackedSearchRef.current = signature;
    trackSearch({
      city: CITY_NAME,
      district: nextDistrict,
      specialty: nextSpecialty,
      nameQuery: name,
      resultsCount: count,
    });
  };

  const fetchPractitioners = async () => {
    setLoading(true);
    setLoadError('');
    try {
      const rows = await fetchPractitionersFromApi();
      setAllPractitioners(rows.map(mapPractitionerRow).sort((a, b) => a.name.localeCompare(b.name, 'fr')));
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Erreur de chargement des praticiens');
    } finally {
      setLoading(false);
    }
  };

  const fetchFilters = async () => {
    try {
      const options = await fetchSearchOptionsFromApi();
      setFilterOptions({
        cities: options.cities.length > 0 ? options.cities : [CITY_NAME],
        specialties: options.specialties.length > 0 ? options.specialties : specialties,
        districts: options.districts.length > 0 ? options.districts : districts,
      });
    } catch {
      setFilterOptions({ cities: [CITY_NAME], specialties, districts });
    }
  };

  useEffect(() => {
    fetchPractitioners();
    fetchFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Resynchronise les filtres quand l'URL change (navigation entre quartiers/spécialités).
  useEffect(() => {
    const nextDistrict = findByNameSlug(filterOptions.districts, params.district) || searchParams.get('district') || '';
    const nextSpecialty = findByNameSlug(filterOptions.specialties, params.specialty) || searchParams.get('specialty') || '';
    setDistrict(nextDistrict);
    setSpecialty(nextSpecialty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.district, params.specialty, spString, filterOptions]);

  const results = useMemo(() => {
    const q = nameQuery.trim().toLowerCase();
    const filtered = allPractitioners.filter((p) => {
      const matchName = !q || p.name.toLowerCase().includes(q);
      const matchType = !specialty || p.type === specialty;
      const matchDistrict = !district || p.district === district;
      return matchName && matchType && matchDistrict;
    });

    // Recherche par nom : le patient choisit lui-même le praticien -> pas de tri par forfait
    if (q) return filtered;

    // Sinon, tri par forfait (Premium > ARS > AF, puis sans-forfait classés par meilleure note),
    // puis disponibilité immédiate, puis note décroissante (meilleurs notés d'abord),
    // et enfin ordre alphabétique pour départager les ex æquo.
    return [...filtered].sort((a, b) => {
      const ta = TIER_ORDER[a.subscriptionTier ?? ''] ?? 3;
      const tb = TIER_ORDER[b.subscriptionTier ?? ''] ?? 3;
      if (ta !== tb) return ta - tb;
      if (a.isAvailableNow !== b.isAvailableNow) return a.isAvailableNow ? -1 : 1;
      const ra = a.rating ?? 0;
      const rb = b.rating ?? 0;
      if (ra !== rb) return rb - ra;
      return a.name.localeCompare(b.name, 'fr');
    });
  }, [allPractitioners, specialty, district, nameQuery]);

  // Envoie un événement de recherche à Google Analytics (ville / quartier / spécialité)
  // à chaque nouvelle combinaison affichée : arrivée depuis un moteur sur une URL SEO
  // comme /rabat/agdal/cardiologue, ou changement de filtre.
  useEffect(() => {
    if (loading) return;
    sendSearchEvent(district, specialty, '', results.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, district, specialty]);

  const totalPages = Math.max(1, Math.ceil(results.length / PAGE_SIZE));

  // Nouveau filtre ou nouvelle recherche : on revient toujours sur la première page.
  useEffect(() => {
    setPage(1);
  }, [specialty, district, nameQuery]);

  // Sécurise l'index courant si le nombre de pages diminue (ex. filtre resserré).
  useEffect(() => {
    setPage((prev) => Math.min(prev, totalPages));
  }, [totalPages]);

  const pageResults = useMemo(
    () => results.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [results, page],
  );

  // Changement de page : remonte en haut de la liste pour éviter de rester
  // tout en bas après avoir cliqué sur une page.
  const handlePageChange = (next: number) => {
    if (next < 1 || next > totalPages || next === page) return;
    setPage(next);
    resultsTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const applyFilters = () => {
    // Enregistre la recherche (y compris une recherche par nom) dans Google Analytics.
    sendSearchEvent(district, specialty, nameQuery, results.length);
    // Quartier sélectionné : on génère une URL propre et indexable
    // (ex. /rabat/agdal/cardiologue) au lieu de simples paramètres.
    if (district) {
      navigate(buildSearchPath(district, specialty));
      return;
    }
    const p = new URLSearchParams();
    if (specialty) p.set('specialty', specialty);
    const qs = p.toString();
    navigate(qs ? `/recherche?${qs}` : '/recherche');
  };

  const resetFilters = () => {
    setSpecialty('');
    setDistrict('');
    setNameQuery('');
    navigate('/recherche');
  };

  // Ville inconnue dans l'URL (ex. /casablanca/agdal) : on renvoie vers la 404.
  if (invalidCity) {
    return <NotFound />;
  }

  return (
    <div className="min-h-screen bg-background-100">
      <header className="sticky top-0 z-40 bg-background-50 border-b border-background-200">
        <div className="px-4 md:px-8 lg:px-12 h-20 flex items-center justify-between">
          <Link to="/">
            <Logo />
          </Link>
          <Link
            to="/"
            className="hidden sm:flex items-center gap-2 px-4 py-2.5 rounded-md bg-foreground-950 hover:bg-foreground-800 text-background-50 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center" />
            Retour au site
          </Link>
        </div>
      </header>

      <main className="px-4 md:px-8 lg:px-12 pt-8 md:pt-10 pb-10 max-w-7xl mx-auto scroll-mt-24">
        <Breadcrumbs district={district} specialty={specialty} />
        <div className="mb-8 scroll-mt-24" ref={resultsTopRef}>
          <span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-primary-600 mb-3">
            Résultats de recherche
          </span>
          <h1 className="font-heading text-2xl md:text-4xl font-semibold text-foreground-950 leading-tight">
            {specialty || 'Tous les praticiens'}
            {district ? ` · ${district}` : ''}
          </h1>
          <p className="text-foreground-600 text-sm md:text-base mt-2">
            <strong className="text-foreground-900">{loading ? '—' : results.length}</strong> praticien{results.length > 1 ? 's' : ''} trouvé{results.length > 1 ? 's' : ''}
            {specialty && district ? ' pour cette recherche.' : '.'}
          </p>
        </div>

        <div className="bg-background-50 rounded-2xl border border-background-200 p-4 md:p-5 mb-8 scroll-mt-24">
          <div className="mb-3">
            <label className="block text-xs font-semibold text-foreground-700 mb-1.5 uppercase tracking-wide">Rechercher par nom</label>
            <div className="relative">
              <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 text-lg w-5 h-5 flex items-center justify-center" />
              <input
                type="text"
                value={nameQuery}
                onChange={(e) => setNameQuery(e.target.value)}
                placeholder="Ex. Dr Amine Benali"
                className="w-full pl-11 pr-3.5 py-3 rounded-md border border-background-300 focus:border-primary-500 focus:outline-none text-sm text-foreground-800"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
            <div className="md:col-span-5 relative">
              <label className="block text-xs font-semibold text-foreground-700 mb-1.5 uppercase tracking-wide">Spécialité</label>
              <button
                onClick={() => { setShowSpec(!showSpec); setShowDist(false); }}
                className="w-full flex items-center justify-between gap-2 px-3.5 py-3 rounded-md border border-background-300 hover:border-primary-400 text-sm text-foreground-800 cursor-pointer overflow-hidden"
              >
                <span className={`truncate min-w-0 ${specialty ? '' : 'text-foreground-500'}`}>
                  {specialty || 'Toutes les spécialités'}
                </span>
                <i className="ri-arrow-down-s-line flex-shrink-0 text-foreground-500 text-lg w-5 h-5 flex items-center justify-center" />
              </button>
              {showSpec && (
                <div className="absolute left-0 right-0 mt-1 bg-background-50 border border-background-200 rounded-md max-h-64 overflow-y-auto z-20">
                  {filterOptions.specialties.map((s) => (
                    <button
                      key={s}
                      onClick={() => { setSpecialty(s); setShowSpec(false); }}
                      className={`w-full text-left px-3.5 py-2.5 text-sm cursor-pointer ${
                        specialty === s ? 'bg-primary-50 text-primary-700 font-medium' : 'text-foreground-800 hover:bg-primary-50'
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="md:col-span-5 relative">
              <label className="block text-xs font-semibold text-foreground-700 mb-1.5 uppercase tracking-wide">Quartier</label>
              <button
                onClick={() => { setShowDist(!showDist); setShowSpec(false); }}
                className="w-full flex items-center justify-between gap-2 px-3.5 py-3 rounded-md border border-background-300 hover:border-primary-400 text-sm text-foreground-800 cursor-pointer overflow-hidden"
              >
                <span className={`truncate min-w-0 ${district ? '' : 'text-foreground-500'}`}>
                  {district || 'Tous les quartiers'}
                </span>
                <i className="ri-map-pin-line flex-shrink-0 text-foreground-500 text-lg w-5 h-5 flex items-center justify-center" />
              </button>
              {showDist && (
                <div className="absolute left-0 right-0 mt-1 bg-background-50 border border-background-200 rounded-md max-h-64 overflow-y-auto z-20">
                  {filterOptions.districts.map((d) => (
                    <button
                      key={d}
                      onClick={() => { setDistrict(d); setShowDist(false); }}
                      className={`w-full text-left px-3.5 py-2.5 text-sm cursor-pointer ${
                        district === d ? 'bg-primary-50 text-primary-700 font-medium' : 'text-foreground-800 hover:bg-primary-50'
                      }`}
                    >
                      {d}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="md:col-span-2 flex items-end gap-2">
              <button
                onClick={applyFilters}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-search-line w-4 h-4 flex items-center justify-center" />
                Filtrer
              </button>
            </div>
          </div>

          {(specialty || district || nameQuery) && (
            <button
              onClick={resetFilters}
              className="mt-3 inline-flex items-center gap-1.5 text-sm text-primary-700 hover:text-primary-800 font-medium cursor-pointer"
            >
              <i className="ri-refresh-line w-4 h-4 flex items-center justify-center" />
              Réinitialiser les filtres
            </button>
          )}
        </div>

        {loading ? (
          <div className="text-center py-20">
            <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full border-2 border-primary-200 border-t-primary-500 animate-spin" />
            <p className="text-sm text-foreground-600 mt-4">Chargement des praticiens…</p>
          </div>
        ) : loadError ? (
          <div className="text-center py-20 bg-background-50 rounded-2xl border border-background-200">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-accent-100 mb-5">
              <i className="ri-error-warning-line text-3xl text-accent-700 w-8 h-8 flex items-center justify-center" />
            </div>
            <h3 className="font-heading text-xl font-semibold text-foreground-950 mb-2">
              Impossible de charger les praticiens
            </h3>
            <p className="text-sm text-foreground-600 max-w-md mx-auto mb-6 leading-relaxed">
              {loadError}
            </p>
            <button
              onClick={fetchPractitioners}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line w-4 h-4 flex items-center justify-center" />
              Réessayer
            </button>
          </div>
        ) : results.length > 0 ? (
          <div>
            <div className="space-y-4">
              {pageResults.map((p) => (
                <PractitionerCard key={p.id} practitioner={p} onBook={setBooking} />
              ))}
            </div>
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
            <p className="text-center text-xs text-foreground-500 mt-4">
              Affichage des praticiens {(page - 1) * PAGE_SIZE + 1} à{' '}
              {Math.min(page * PAGE_SIZE, results.length)} sur {results.length}
            </p>
          </div>
        ) : (
          <div className="text-center py-20 bg-background-50 rounded-2xl border border-background-200">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-secondary-100 mb-5">
              <i className="ri-emotion-sad-line text-3xl text-secondary-700 w-8 h-8 flex items-center justify-center" />
            </div>
            <h3 className="font-heading text-xl font-semibold text-foreground-950 mb-2">
              Aucun praticien trouvé
            </h3>
            <p className="text-sm text-foreground-600 max-w-md mx-auto mb-6 leading-relaxed">
              Aucun résultat pour cette combinaison de spécialité et de quartier.
              Essayez d'élargir vos critères.
            </p>
            <button
              onClick={resetFilters}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line w-4 h-4 flex items-center justify-center" />
              Voir tous les praticiens
            </button>
          </div>
        )}
      </main>

      {booking && (
        <BookingModal practitioner={booking} onClose={() => setBooking(null)} />
      )}
    </div>
  );
}